/**
* Branch network types + factory.
*
* A "branch" is any physical service point where clients can meet a licensed
* Notarial Services Attorney, drop off documents, or receive a mobile visit.
* The dataset is the single source of truth for:
* - NAP-W consistency across Header / Footer / Contact
* - schema.org LocalBusiness / Organization.subOrganization graph
* - /coverage/$province and /coverage/bangkok/$district local blocks
* - /branches hub and /branches/$branch local landing pages
* - AEO feeds (llms-full.txt, bing/claude/perplexity)
*/

export type BranchType =
  | "hq"
  | "district"
  | "province"
  | "mall"
  | "airport"
  | "border"
  | "government"
  | "visa-center"
  | "expat-hub"
  | "marina"
  | "overseas";

/** Service cluster ids — must match the route clusters that exist on the site. */
export type ServiceFocus =
  | "notary"
  | "translation"
  | "sworn"
  | "naati"
  | "lgz"
  | "moj"
  | "pcc"
  | "visa"
  | "longstay"
  | "marriage"
  | "corporate"
  | "immigration"
  | "estate"
  | "civilreg"
  | "apostille";

export interface Branch {
  /** URL-safe unique id — also the /branches/$branch slug. */
  id: string;
  name: string;
  shortName: string;
  /** Street line. Empty string when the point is service-area-only. */
  street: string;
  locality: string;
  region: string;
  postalCode: string;
  country: string;
  phone: string;
  hours: string;
  geo?: { lat: number; lng: number };
  branchType: BranchType;
  /** Joins to PROVINCES in src/data/coverage.ts */
  provinceSlug?: string;
  /** Joins to BANGKOK_DISTRICTS in src/data/coverage.ts */
  districtSlug?: string;
  landmarks: string[];
  transit: string[];
  serviceFocus: ServiceFocus[];
  /**
   * True when we serve the area by appointment / mobile visit without a
   * full street-numbered office. Such points are emitted as `areaServed`
   * rather than a `PostalAddress` so the NAP data stays truthful.
   */
  serviceAreaOnly?: boolean;
  /**
   * Neighbourhood-level descriptor of where we meet clients by appointment.
   * NEVER a street number — service-area points must not advertise an office
   * address they do not staff (Google local-spam policy).
   */
  meetingPoint?: string;
  isHeadquarters?: boolean;
  /** Official Google Maps link — only for staffed offices with a real address. */
  mapUrl?: string;
  /** One-line differentiator used in copy + meta descriptions. */
  note?: string;
}

export const DEFAULT_PHONE = "+66-94-895-8999";
export const DEFAULT_HOURS = "Mo-Su 09:00-18:00";

type BranchInput = Omit<Branch, "phone" | "hours" | "country" | "landmarks" | "transit" | "serviceFocus"> &
  Partial<Pick<Branch, "phone" | "hours" | "country" | "landmarks" | "transit" | "serviceFocus">>;

/**
* Factory that fills the shared NAP-W defaults and enforces the compliance
* rule: a service-area point can never carry a street line or postcode.
*/
export function branch(input: BranchInput): Branch {
  const b: Branch = {
    phone: DEFAULT_PHONE,
    hours: DEFAULT_HOURS,
    country: "TH",
    landmarks: [],
    transit: [],
    serviceFocus: ["notary", "translation", "lgz"],
    ...input,
  };
  if (b.serviceAreaOnly) {
    b.street = "";
    b.postalCode = "";
    b.mapUrl = undefined;
  }
  return b;
}

/** True only when the point has a verifiable staffed street address. */
export function hasPostalAddress(b: Pick<Branch, "street" | "serviceAreaOnly">) {
  return !b.serviceAreaOnly && b.street.trim().length > 0;
}

/** Human-readable location line — never prints a fabricated street. */
export function locationLine(b: Branch) {
  if (hasPostalAddress(b)) {
    return `${b.street}, ${b.locality}, ${b.region} ${b.postalCode}`.trim();
  }
  const where = b.meetingPoint ? `${b.meetingPoint}, ${b.locality}` : b.locality;
  return `พื้นที่ให้บริการ: ${where}, ${b.region} (นัดหมายล่วงหน้า / นอกสถานที่)`;
}


export const BRANCH_TYPE_LABEL: Record<BranchType, string> = {
  hq: "สำนักงานใหญ่",
  district: "สาขาประจำเขต",
  province: "สาขาประจำจังหวัด",
  mall: "จุดบริการในศูนย์การค้า",
  airport: "จุดบริการสนามบิน",
  border: "จุดบริการด่านชายแดน",
  government: "จุดบริการใกล้หน่วยราชการ",
  "visa-center": "จุดบริการศูนย์ยื่นวีซ่า",
  "expat-hub": "จุดบริการชุมชนชาวต่างชาติ",
  marina: "จุดบริการท่าเรือมารีน่า",
  overseas: "สาขาต่างประเทศ",
};

export const SERVICE_FOCUS_LABEL: Record<ServiceFocus, string> = {
  notary: "Notary Public",
  translation: "แปลเอกสารรับรอง",
  sworn: "Sworn Translation",
  naati: "NAATI Translation",
  lgz: "รับรองกงสุล / สถานทูต",
  moj: "รับรองกระทรวงยุติธรรม",
  pcc: "ใบรับรองความประพฤติ (PCC)",
  visa: "วีซ่า / Work Permit",
  longstay: "วีซ่าพำนักระยะยาว",
  marriage: "จดทะเบียนสมรส / Marriage Visa",
  corporate: "จดทะเบียนบริษัท / นิติบุคคล",
  immigration: "ตรวจคนเข้าเมือง",
  estate: "มรดก / พินัยกรรม",
  civilreg: "ทะเบียนราษฎร์",
  apostille: "Apostille",
};

/** Service-cluster landing paths used for internal linking from branch pages. */
export const SERVICE_FOCUS_PATH: Record<ServiceFocus, string> = {
  notary: "/notary",
  translation: "/translation",
  sworn: "/sworn",
  naati: "/naati",
  lgz: "/lgz",
  moj: "/moj",
  pcc: "/pcc",
  visa: "/visa",
  longstay: "/longstay",
  marriage: "/marriage",
  corporate: "/corporate",
  immigration: "/immigration",
  estate: "/estate",
  civilreg: "/civil-registration",
  apostille: "/apostille",
};
